import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { createPlaidLinkToken, exchangePlaidToken } from 'services/plaid';

const SHIELD = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

/**
 * PlaidLinkButton — connects/verifies a bank account via Plaid Link.
 * Link token is created lazily on button click (not on mount) to avoid
 * flooding the API when multiple buttons are on the same page.
 *
 * Props:
 *   userId        {string}   — unique ID for the Plaid user
 *   bankAccountId {string}   — existing BankAccount DB record ID
 *   ownerType     {string}   — "employee" | "contractor" | "company"
 *   companyId     {string}   — required when ownerType === "company"
 *   onSuccess     {function} — called with API result on success
 *   onError       {function} — called with error message string
 *   disabled      {boolean}
 *   label         {string}   — button label override
 */
export default function PlaidLinkButton({
    userId,
    ownerId,        // employee/contractor ID for creating a NEW bank account
    bankAccountId,  // existing BankAccount DB record ID (for verifying existing accounts)
    ownerType = 'employee',
    companyId,
    onSuccess,
    onError,
    disabled = false,
    label,
}) {
    const [linkToken, setLinkToken]   = useState(null);
    const [loading, setLoading]       = useState(false);
    const [shouldOpen, setShouldOpen] = useState(false);
    const [status, setStatus]         = useState(null); // null | 'verified' | 'error'
    const [errorMsg, setErrorMsg]     = useState('');

    // Called when user confirms bank connection via Plaid Link
    const onPlaidSuccess = useCallback(async (publicToken) => {
        setLoading(true);
        setStatus(null);
        try {
            const result = await exchangePlaidToken({ publicToken, bankAccountId, ownerId: ownerId || userId, ownerType, companyId });
            setStatus('verified');
            if (onSuccess) onSuccess(result);
        } catch (err) {
            const msg = err?.response?.data?.error || 'Bank verification failed. Please try again.';
            setErrorMsg(msg);
            setStatus('error');
            if (onError) onError(msg);
        } finally {
            setLoading(false);
        }
    }, [bankAccountId, ownerId, userId, ownerType, companyId, onSuccess, onError]);

    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: onPlaidSuccess,
        onExit: (err) => {
            setShouldOpen(false);
            if (err) {
                setStatus('error');
                setErrorMsg(err.display_message || 'Bank connection cancelled or failed.');
                if (onError) onError(errorMsg);
            }
        },
    });

    // Once we have a token and Plaid Link is ready, open it
    useEffect(() => {
        if (shouldOpen && linkToken && ready) {
            setShouldOpen(false);
            open();
        }
    }, [shouldOpen, linkToken, ready, open]);

    // Fetch link token on click, then open Plaid Link
    const handleClick = async () => {
        if (!userId || loading) return;
        setLoading(true);
        setStatus(null);
        setErrorMsg('');
        try {
            const token = await createPlaidLinkToken(userId);
            setLinkToken(token);
            setShouldOpen(true); // will open once usePlaidLink is ready with the new token
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to initialize bank verification.';
            setErrorMsg(msg);
            setStatus('error');
            if (onError) onError(msg);
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = disabled || loading || !userId;

    if (status === 'verified') {
        return (
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#f0fdf4', border: '1.5px solid #86efac',
                borderRadius: 8, padding: '8px 16px', fontSize: 13, color: '#166534', fontWeight: 600,
            }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
                Bank account verified
            </div>
        );
    }

    return (
        <div>
            <button
                type="button"
                onClick={handleClick}
                disabled={isDisabled}
                style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '9px 20px', borderRadius: 8, border: 'none',
                    background: !isDisabled ? '#1B75F0' : '#e5e7eb',
                    color: !isDisabled ? '#fff' : '#9ca3af',
                    fontSize: 13, fontWeight: 700,
                    cursor: !isDisabled ? 'pointer' : 'not-allowed',
                    transition: 'background 0.15s',
                }}
            >
                {SHIELD}
                {loading ? 'Loading...' : (label || 'Connect Bank Account')}
            </button>

            {status === 'error' && (
                <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6, fontWeight: 500 }}>
                    {errorMsg}
                </p>
            )}

            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                Secured by Plaid — your credentials are never shared with us.
            </p>
        </div>
    );
}
